'use client';

import React from 'react';
import { clsx } from 'clsx';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@src/components/ui/pagination';
import type { PaginationComponentType } from '@src/v3/interfaces/components/coreUI/pagination';

/**
 * Reusable Pagination Component
 *
 * A customizable pagination component built on top of shadcn UI components.
 * Features include:
 * - Configurable number of visible pages
 * - Optional ellipsis for large page counts
 * - Previous/Next navigation buttons
 * - Custom styling with consistent design system
 * - Accessibility support through shadcn components
 *
 * @param currentPage - Current active page number (1-based)
 * @param totalPages - Total number of pages
 * @param onPageChange - Callback function when page changes
 * @param maxVisiblePages - Maximum number of page buttons to show (default: 4)
 * @param showNext - Whether to show next button (default: false)
 * @param showPrevious - Whether to show previous button (default: false)
 * @param extraClasses - Additional CSS classes for container
 * @param disabled - Whether pagination is disabled
 * @param enableResponsiveLayout - Whether to enable responsive layout with flex-wrap and justify-center (default: false)
 *
 * @returns React.ReactNode - Pagination component or null if totalPages <= 1
 */
const PaginationComponent = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {
    return;
  },
  maxVisiblePages = 4,
  showNext = true,
  showPrevious = true,
  extraClasses = '',
  disabled = false,
  enableResponsiveLayout = false,
}: PaginationComponentType) => {
  // Don't render if only one page or invalid props
  if (totalPages <= 1 || currentPage < 1 || currentPage > totalPages) {
    return null;
  }

  const containerClassName = clsx(
    'mt-8 w-full flex justify-center',
    extraClasses
  );

  const pageButtonClassName = (isActive: boolean) =>
    clsx(
      'p-4 rounded-full border-0 text-base font-semibold transition-colors cursor-pointer',
      {
        'bg-black-dark text-white hover:bg-black-light hover:text-white':
          isActive,
        'bg-transparent text-black-dark hover:bg-transparent hover:text-black':
          !isActive,
        'opacity-50 cursor-not-allowed': disabled,
      }
    );

  const navButtonClassName = clsx(
    'rounded-full border-0 bg-transparent text-black-dark font-semibold hover:bg-transparent hover:text-black transition-colors cursor-pointer',
    {
      'opacity-50 cursor-not-allowed': disabled,
    }
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handlePageClick = (page: number) => {
    if (!disabled && page !== currentPage) {
      onPageChange(page);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    if (!disabled && currentPage > 1) {
      onPageChange(currentPage - 1);
      scrollToTop();
    }
  };

  const handleNext = () => {
    if (!disabled && currentPage < totalPages) {
      onPageChange(currentPage + 1);
      scrollToTop();
    }
  };

  const paginationContentClassName = clsx('gap-1', {
    'flex-wrap justify-center': enableResponsiveLayout,
  });

  return (
    <div className={containerClassName}>
      <Pagination className="w-full">
        <PaginationContent className={paginationContentClassName}>
          {/* Previous button */}
          {showPrevious && currentPage > 1 && (
            <PaginationItem>
              <PaginationPrevious
                onClick={handlePrevious}
                className={`${navButtonClassName} px-0`}
              />
            </PaginationItem>
          )}

          {/* Dynamic page range around current page */}
          {(() => {
            const pages = [];

            // Always show page 1 if not in visible range
            if (currentPage > 2) {
              pages.push(
                <PaginationItem key={1}>
                  <PaginationLink
                    onClick={() => handlePageClick(1)}
                    isActive={false}
                    className={pageButtonClassName(false)}
                  >
                    1
                  </PaginationLink>
                </PaginationItem>
              );

              // Show ellipsis if gap between page 1 and current range
              if (currentPage > 3) {
                pages.push(
                  <PaginationItem key="ellipsis-start">
                    <PaginationEllipsis className="text-black-dark" />
                  </PaginationItem>
                );
              }
            }

            // Show pages around current page
            // For early pages (1-3), show at least first 4 pages
            // For last pages, show at least last 4 pages
            let startPage, endPage;
            if (currentPage <= 3) {
              // Early pages: show first 4 pages
              startPage = 1;
              endPage = Math.min(totalPages, maxVisiblePages);
            } else if (currentPage >= totalPages - 2) {
              // Last pages: show last 4 pages
              startPage = Math.max(1, totalPages - maxVisiblePages + 1);
              endPage = totalPages;
            } else {
              // Middle pages: show current-1, current, current+1
              startPage = Math.max(1, currentPage - 1);
              endPage = Math.min(totalPages, currentPage + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
              // Don't duplicate page 1 or last page
              if (
                !(i === 1 && currentPage > 2) &&
                !(i === totalPages && currentPage < totalPages - 1)
              ) {
                pages.push(
                  <PaginationItem key={i}>
                    <PaginationLink
                      onClick={() => handlePageClick(i)}
                      isActive={i === currentPage}
                      className={pageButtonClassName(i === currentPage)}
                    >
                      {i}
                    </PaginationLink>
                  </PaginationItem>
                );
              }
            }

            // Always show last page if not in visible range
            if (currentPage < totalPages - 1) {
              // Show ellipsis if gap between current range and last page
              if (currentPage < totalPages - 2) {
                pages.push(
                  <PaginationItem key="ellipsis-end">
                    <PaginationEllipsis className="text-black-dark" />
                  </PaginationItem>
                );
              }

              pages.push(
                <PaginationItem key={totalPages}>
                  <PaginationLink
                    onClick={() => handlePageClick(totalPages)}
                    isActive={false}
                    className={pageButtonClassName(false)}
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              );
            }

            return pages;
          })()}

          {/* Next button */}
          {showNext && currentPage < totalPages && (
            <PaginationItem>
              <PaginationNext
                onClick={handleNext}
                className={navButtonClassName}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default PaginationComponent;
